import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { REHYDRATE } from "redux-persist";

import store, { IAppState } from "../Store";
import Datastore from "../../communication/Datastore";
import Group from "../../types/archive/Group";

// Define the type for our state store entry
type GroupState = {
    groups: Group[];
    status: string;
    error: string;
};

// Define the initial state for our state store entry
const initialState: GroupState = {
    groups: [],
    status: "idle",
    error: null,
};

// Create a slice to hold all our reducers
const groupSlice = createSlice({
    name: "group",
    initialState: initialState,
    reducers: {
        resetGroupStatus(state) {
            state.status = "idle";
        },
    },
    extraReducers(builder) {
        builder
            .addCase(REHYDRATE, (_, rawData: any) => {
                if (rawData?.key !== "tts") return undefined;
                return {
                    groups:
                        rawData?.payload?.groups?.groups?.map(
                            c => new Group(c),
                        ) || [],
                    status: !rawData?.payload?.groups?.groups
                        ? "idle"
                        : "succeeded",
                    error: null,
                };
            })
            .addCase(fetchGroups.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(fetchGroups.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.groups = action.payload.map(c => new Group(c));
            })
            .addCase(fetchGroups.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(addGroup.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(addGroup.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                state.groups = state.groups.concat(new Group(action.payload));
            })
            .addCase(addGroup.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            });
    },
});

const { resetGroupStatus } = groupSlice.actions;

export const initialiseGroupDetails = createAsyncThunk(
    "group/initialiseGroupDetails",
    (_, thunkAPI) => thunkAPI.dispatch(fetchGroups()),
);

// This is a thunk, they are used to load the state
export const fetchGroups = createAsyncThunk(
    "group/fetchGroups",
    () => Datastore.getType<Group[]>("api/Timetables/Groups"),
    {
        condition: (_, api) =>
            store.getState().db.groups.status === "idle",
    },
);
export const addGroup = createAsyncThunk(
    "group/addGroup",
    (payload: Group) =>
        Datastore.postType<Group>("api/Timetables/Groups", payload),
    {
        condition: (_, api) =>
            store.getState().db.groups.status === "succeeded",
    },
);

// We have to export the reducer as this is added to the state store
export default groupSlice.reducer;
